/**
 * "Did Cora get this right?" — one verdict per recruiter per candidate, with an optional reason.
 * Disagreements are what the Quality tab counts and what future prompt changes are tested against.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { submitCoraFeedback, type CoraFeedback } from '../../services/coraApi';
import { fmtWhen } from './coraFormat';

export default function FeedbackForm({ runId, feedback, onSaved }: { runId: string; feedback: CoraFeedback[]; onSaved: () => void }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (verdict: 'agree' | 'disagree') => {
    setBusy(true);
    try {
      await submitCoraFeedback(runId, verdict, comment);
      void logAnalyticsEvent('cora_feedback_submitted', { verdict, with_comment: Boolean(comment.trim()) });
      showToast({ status: 'success', description: 'Thanks — your verdict on Cora is saved.' });
      setComment('');
      onSaved();
    } catch (err) {
      void logAnalyticsEvent('cora_feedback_failed', { verdict });
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Could not save your verdict.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 border-t border-solid border-pearl pt-4">
      <h3 className="text-sm font-semibold text-zinc-900">Did Cora get this right?</h3>
      {feedback.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-zinc-700">
          {feedback.map((f) => (
            <li key={f.by}>
              {f.verdict === 'agree' ? '👍' : '👎'} {f.by} · {fmtWhen(f.at)}
              {f.comment ? ` — ${f.comment}` : ''}
            </li>
          ))}
        </ul>
      )}
      <Textarea
        label="What did she miss or get wrong? (optional)"
        value={comment}
        maxLength={1000}
        onChange={(e) => setComment((e.target as HTMLTextAreaElement).value)}
      />
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => send('agree')} disabled={busy}>👍 Right</Button>
        <Button variant="outline" onClick={() => send('disagree')} disabled={busy}>👎 Wrong</Button>
      </div>
    </section>
  );
}
